/**
 * WhatsApp Communication API
 *
 * Sends messages and fetches communication history. All endpoints require
 * authenticated super_admin or staff user. Base URL is prefixed with user_type
 * (e.g. /super_admin/whatsapp) via axios interceptor.
 */
import sendApiRequest from '../helpers/axios-setup';

/**
 * Send a WhatsApp message to recipients.
 *
 * @param {Object} data - Payload
 * @param {string} data.recipientType - 'child' | 'batch' | 'phone'
 * @param {string} [data.messageType] - 'text' | 'template' (default: 'text')
 * @param {string[]} [data.recipientIds] - Required for child/batch; array of child or batch IDs
 * @param {string[]} [data.phoneNumbers] - Required for phone; array of Indian mobile numbers (E.164)
 * @param {string} [data.message] - Required for text type
 * @param {string} [data.templateId] - Required for template type; MongoDB _id of template
 * @param {string} [data.purpose] - Optional label describing the reason for this send (e.g. "Fee reminder - April")
 * @returns {Promise<Object>} Response data with id, status, totalRecipients, successCount, failedCount
 * @throws {Error} On network/API error; error.response.data.message may contain validation message
 */
export async function sendWhatsAppMessage(data) {
  try {
    const response = await sendApiRequest.post('/whatsapp/send', data);
    return response.data.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch WhatsApp message templates (for dropdown).
 * @returns {Promise<Array>} Array of { _id, name, language }
 */
export async function getWhatsAppTemplates() {
  try {
    const response = await sendApiRequest.get('/whatsapp/templates');
    return response.data.data || [];
  } catch (error) {
    throw error;
  }
}

/**
 * Create a WhatsApp message template.
 * @param {Object} data - { name: string } - Meta template name (e.g. hello_world)
 * @returns {Promise<Object>} Created template
 */
export async function createWhatsAppTemplate(data) {
  try {
    const response = await sendApiRequest.post('/whatsapp/templates', data);
    return response.data.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch WhatsApp communication history with pagination and optional filters.
 *
 * @param {Object} params - Query params
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Page size
 * @param {string} [params.recipientType] - Filter by recipientType
 * @param {string} [params.messageType] - Filter by messageType (text | template)
 * @param {string} [params.templateName] - Partial match on template name
 * @param {string} [params.purpose] - Partial match on the send purpose label
 * @param {string} [params.startTime] - Filter start date
 * @param {string} [params.endTime] - Filter end date
 * @param {string} [params.sort_option] - e.g. 'createdAt$desc'
 * @returns {Promise<Object>} { data: Array, metadata: { page, limit, total_pages, total_results } }
 * @throws {Error} On network/API error
 */
export async function fetchWhatsAppCommunications(params) {
  try {
    const response = await sendApiRequest.get('/whatsapp', { params });
    return response.data.data[0];
  } catch (error) {
    throw error;
  }
}

/**
 * Get a single WhatsApp communication by ID.
 *
 * @param {string} id - MongoDB ObjectId of the communication
 * @returns {Promise<Object>} Full communication object with recipientType, message, status, phoneNumbers, etc.
 * @throws {Error} On network/API error or 404 if not found
 */
export async function getWhatsAppCommunicationById(id) {
  try {
    const response = await sendApiRequest.get(`/whatsapp/${id}`);
    return response.data.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch the unified per-phone message log (outbound + inbound) with pagination and filters.
 *
 * @param {Object} params
 * @param {number} [params.page]
 * @param {number} [params.limit]
 * @param {string} [params.phoneNumber] - Partial or full mobile number
 * @param {string} [params.startTime]
 * @param {string} [params.endTime]
 * @param {string} [params.status] - Comma-separated statuses for OR filtering: sent | delivered | read | failed | received
 * @param {string} [params.direction] - outbound | inbound
 * @param {string} [params.purpose] - Partial match on the send purpose label
 * @param {string} [params.sort_option] - sentAt$desc | sentAt$asc
 * @returns {Promise<Object>} { data: Array, metadata: { page, limit, total_pages, total_results } }
 */
/**
 * Fetch send counts for today / this week / this month.
 * @returns {Promise<{today: number, thisWeek: number, thisMonth: number}>}
 */
export async function fetchWhatsAppStats() {
  try {
    const response = await sendApiRequest.get('/whatsapp/stats');
    return response.data.data;
  } catch (error) {
    throw error;
  }
}

export async function fetchWhatsAppMessageLogs(params) {
  try {
    const response = await sendApiRequest.get('/whatsapp/logs', { params });
    return response.data.data[0];
  } catch (error) {
    throw error;
  }
}

const EXPORT_PAGE_LIMIT = 100;
const LARGE_EXPORT_THRESHOLD = 1000;

export const LARGE_EXPORT_DATE_REQUIRED_MSG =
  'More than 1,000 messages match your filters. Please set both From Date and To Date before exporting.';

/**
 * Build query params for GET /whatsapp/logs from Message History filters.
 */
export function buildWhatsAppMessageLogParams(filters, sort_option, page, limit) {
  return {
    page,
    limit,
    sort_option,
    childId: filters.childId || '',
    phoneNumber: filters.phoneNumber || '',
    startTime: filters.startTime || '',
    endTime: filters.endTime || '',
    status: filters.statuses?.length ? filters.statuses.join(',') : '',
    direction: filters.direction || '',
    purpose: filters.purpose || ''
  };
}

/**
 * Fetch all message logs matching filters (paginated server requests).
 * @param {Object} options
 * @param {Object} options.filters - Message History filter state
 * @param {string} options.sort_option - e.g. sentAt$desc
 * @param {Function} [options.onProgress] - Called with 0–70 during fetch
 * @returns {Promise<Array>} All matching log rows
 */
export async function exportWhatsAppMessageLogs({ filters, sort_option, onProgress }) {
  try {
    const firstPage = await fetchWhatsAppMessageLogs(
      buildWhatsAppMessageLogParams(filters, sort_option, 1, EXPORT_PAGE_LIMIT)
    );

    const totalResults = firstPage?.metadata?.total_results || 0;
    const totalPages = firstPage?.metadata?.total_pages || 0;

    if (
      totalResults > LARGE_EXPORT_THRESHOLD &&
      (!filters.startTime || !filters.endTime)
    ) {
      throw new Error(LARGE_EXPORT_DATE_REQUIRED_MSG);
    }

    const allLogs = [...(firstPage?.data || [])];

    if (onProgress) {
      onProgress(totalPages <= 1 ? 70 : Math.max(10, Math.round((1 / totalPages) * 70)));
    }

    for (let page = 2; page <= totalPages; page++) {
      const response = await fetchWhatsAppMessageLogs(
        buildWhatsAppMessageLogParams(filters, sort_option, page, EXPORT_PAGE_LIMIT)
      );
      allLogs.push(...(response?.data || []));
      if (onProgress) {
        onProgress(Math.min(70, Math.max(10, Math.round((page / totalPages) * 70))));
      }
    }

    return allLogs;
  } catch (error) {
    if (error instanceof Error && error.message === LARGE_EXPORT_DATE_REQUIRED_MSG) {
      throw error;
    }
    throw error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Export failed';
  }
}
